import { useEffect, useMemo, useRef, useState } from "react";
import type { AcademicDateCategory, Post } from "../data/models";
import { useServices } from "../data/services";
import { useAsync } from "../data/useAsync";
import { assetUrl } from "../data/assets";
import { absoluteDay, compactCount, count, eventWhen } from "../data/format";
import { haptic, isHosted, openExternal, shareToStory, showAlert } from "../platform/telegram";
import { useNav, type HomeSection } from "../nav/router";
import { Screen } from "../nav/Shell";
import { Avatar, Button, Card, CourseBadge, Pill } from "../components/primitives";
import { PostCard } from "../components/PostCard";
import { AppGate } from "../components/AppGate";
import { EmptyState, ErrorState, FeedSkeleton, Skeleton } from "../components/states";
import { LearnFeed } from "../learn/LearnFeed";
import { fetchCourses, parseLearnPath, type CoursesState } from "../learn/api";
import { fetchWaitlist, type WaitlistEntry } from "../api/profile";
import { makePostActions } from "./postActions";
import "./home.css";

/**
 * Home — the five content sections behind one chip row, per "05 Home".
 *
 * Feed / Explore / Learn / Earn / Compete are sections, not screens: switching
 * chips swaps content in place under the same rail. Calendar and Search are the
 * two pushed screens, reached from Dash-style affordances and the FroshBot
 * navigate action.
 */

/** Telegram keeps its section set; the browser mirrors the iOS Home sub-tabs
 * (Dash · Learn · Earn · Compete · Explore) — only Dash and Learn work there,
 * the rest are the full-app gate (decision 8 Aug 2026). */
const TG_SECTIONS: { id: HomeSection; label: string }[] = [
  { id: "feed", label: "Feed" },
  { id: "explore", label: "Explore" },
  { id: "learn", label: "Learn" },
  { id: "earn", label: "Earn" },
  { id: "compete", label: "Compete" },
];

const WEB_SECTIONS: { id: HomeSection; label: string }[] = [
  { id: "dash", label: "Dash" },
  { id: "learn", label: "Learn" },
  { id: "earn", label: "Earn" },
  { id: "compete", label: "Compete" },
  { id: "explore", label: "Explore" },
];

export function HomeTab() {
  const { homeSection, setHomeSection, push } = useNav();

  if (isHosted) {
    return (
      <Screen>
        <div className="chips" role="tablist">
          {TG_SECTIONS.map((s) => (
            <button
              key={s.id}
              type="button"
              role="tab"
              aria-selected={s.id === homeSection}
              className={`chip${s.id === homeSection ? " chip--active" : ""}`}
              onClick={() => {
                haptic.select();
                setHomeSection(s.id);
              }}
            >
              {s.label}
            </button>
          ))}
          <span style={{ flex: 1 }} />
          <button
            type="button"
            className="chip"
            aria-label="Calendar"
            onClick={() => push({ name: "calendar" })}
          >
            <i className="fa-regular fa-calendar" aria-hidden="true" />
          </button>
          <button
            type="button"
            className="chip"
            aria-label="Search"
            onClick={() => push({ name: "search" })}
          >
            <i className="fa-solid fa-magnifying-glass" aria-hidden="true" />
          </button>
        </div>

        {homeSection === "feed" ? <FeedSection /> : null}
        {homeSection === "explore" ? <ExploreSection /> : null}
        {homeSection === "learn" ? <LearnSection /> : null}
        {homeSection === "earn" ? <EarnSection /> : null}
        {homeSection === "compete" ? <CompeteSection /> : null}
      </Screen>
    );
  }

  // Browser build: the iOS HomeTabView chrome — a floating bar with `+` on the
  // left, the five section labels in the middle (active = heavier and larger,
  // exactly the semibold-headline-vs-body move), search on the right. Solid
  // with a divider on Dash; translucent over the content when Learn is up
  // (Learn is always immersive). `+` and search open the full-app gate — the
  // route name is irrelevant, every pushed route renders the gate in a browser.
  const immersive = homeSection === "learn";
  return (
    <div className="home-web">
      <div className={`homebar${immersive ? " homebar--float" : ""}`}>
        <button
          type="button"
          className="homebar__side"
          aria-label="Create"
          onClick={() => push({ name: "search" })}
        >
          <i className="fa-solid fa-plus" aria-hidden="true" />
        </button>
        <div className="homebar__sections" role="tablist">
          {WEB_SECTIONS.map((s) => (
            <button
              key={s.id}
              type="button"
              role="tab"
              aria-selected={s.id === homeSection}
              className={`homebar__section${s.id === homeSection ? " homebar__section--active" : ""}`}
              onClick={() => {
                haptic.select();
                setHomeSection(s.id);
              }}
            >
              {s.label}
            </button>
          ))}
        </div>
        <button
          type="button"
          className="homebar__side"
          aria-label="Search"
          onClick={() => push({ name: "search" })}
        >
          <i className="fa-solid fa-magnifying-glass" aria-hidden="true" />
        </button>
      </div>
      <div className="home-web__content">
        {homeSection === "dash" ? (
          <div className="home-web__pad">
            <DashSection />
          </div>
        ) : null}
        {homeSection === "learn" ? <LearnSection /> : null}
        {homeSection === "earn" || homeSection === "compete" || homeSection === "explore" ? (
          <AppGate />
        ) : null}
      </div>
    </div>
  );
}

/* ---------------- Dash (browser only) ---------------- */

interface DashWidgetSpec {
  icon: string;
  title: string;
  tone: string;
}

/** The seven iOS Dash widgets that need the full app. They render in the iOS
 * card style, locked, valued "In the app", and tap through to the gate. */
const LOCKED_WIDGETS: DashWidgetSpec[] = [
  { icon: "fa-bolt", title: "Today's Challenge", tone: "var(--warning)" },
  { icon: "fa-calendar-days", title: "Events", tone: "var(--blue-600)" },
  { icon: "fa-robot", title: "Froshbot", tone: "var(--mint-500)" },
  { icon: "fa-coins", title: "Frosh Points", tone: "var(--warning)" },
  { icon: "fa-trophy", title: "Campus Rank", tone: "var(--blue-500)" },
  { icon: "fa-heart", title: "New Matches", tone: "var(--error)" },
  { icon: "fa-envelope", title: "Unread", tone: "var(--blue-500)" },
];

function DashWidget({
  icon,
  title,
  tone,
  value,
  locked = false,
  onClick,
}: DashWidgetSpec & { value: string; locked?: boolean; onClick: () => void }) {
  return (
    <button type="button" className={`dw${locked ? " dw--locked" : ""}`} onClick={onClick}>
      <span
        className="dw__icon"
        style={{ color: tone, background: `color-mix(in srgb, ${tone} 14%, transparent)` }}
      >
        <i className={`fa-solid ${icon}`} aria-hidden="true" />
      </span>
      {locked ? <i className="fa-solid fa-lock dw__lock" aria-hidden="true" /> : null}
      <span className="dw__title">{title}</span>
      <span className="dw__value">{value}</span>
    </button>
  );
}

/**
 * The web Dash — the iOS DashView structure (calendar strip, then the 2-column
 * widget grid), overview only, and only *real* data. The week strip shows the
 * actual week with today highlighted and invents no entries. Two widgets are
 * live — Your Courses (real picks, taps into Learn) and Waitlist (real rank);
 * the other seven are the iOS set, locked, and tap through to the gate.
 */
function DashSection() {
  const { setHomeSection, push } = useNav();
  const [courses, setCoursesState] = useState<CoursesState | null>(null);
  const [waitlist, setWaitlist] = useState<WaitlistEntry | null>(null);

  useEffect(() => {
    void fetchCourses().then(setCoursesState);
    void fetchWaitlist().then(setWaitlist);
  }, []);

  const week = useMemo(() => {
    const days: Date[] = [];
    const base = new Date();
    for (let i = 0; i < 7; i++) days.push(new Date(base.getTime() + i * 86_400_000));
    return days;
  }, []);

  const picked = courses?.selected.length ?? 0;
  const openGate = () => push({ name: "search" });

  return (
    <div className="dash">
      <div className="week-strip">
        {week.map((d, i) => (
          <div key={d.toISOString()} className={`week-day${i === 0 ? " week-day--active" : ""}`}>
            <span className="week-day__name">
              {d.toLocaleDateString(undefined, { weekday: "short" })}
            </span>
            <span className="week-day__num">{d.getDate()}</span>
          </div>
        ))}
      </div>

      <div className="dash__grid">
        <DashWidget
          icon="fa-book"
          title="Your Courses"
          tone="var(--blue-500)"
          value={picked > 0 ? `${picked} picked` : "None yet"}
          onClick={() => setHomeSection("learn")}
        />
        <DashWidget
          icon="fa-list-check"
          title="Waitlist"
          tone="var(--mint-600)"
          value={waitlist ? `#${waitlist.rank} · ${waitlist.school}` : "Join the list"}
          onClick={openGate}
        />
        {LOCKED_WIDGETS.map((w) => (
          <DashWidget key={w.title} {...w} value="In the app" locked onClick={openGate} />
        ))}
      </div>
    </div>
  );
}

/* ---------------- Feed ---------------- */

function FeedSection() {
  const { feed } = useServices();
  const { push } = useNav();
  const load = useAsync(() => feed.getForYouFeed(0, 8), []);

  const [posts, setPosts] = useState<Post[]>([]);
  useEffect(() => {
    if (load.status === "ready") setPosts(load.data);
  }, [load.status, load.data]);

  const actions = useMemo(
    () => makePostActions(feed, setPosts, (post) => push({ name: "postDetail", postId: post.id })),
    [feed, push],
  );

  if (load.status === "loading") return <FeedSkeleton />;
  if (load.status === "error") {
    return <ErrorState title="Your feed didn’t load" onRetry={load.reload} />;
  }
  if (posts.length === 0) {
    return (
      <EmptyState
        title="Your feed starts at orientation"
        body="Campus posts unlock when your class's space opens. Meanwhile, matching is live."
      />
    );
  }

  return (
    <div className="stack">
      {posts.map((p) => (
        <PostCard key={p.id} post={p} actions={actions} />
      ))}
    </div>
  );
}

/* ---------------- Explore ---------------- */

function ExploreSection() {
  const { feed } = useServices();
  const { push } = useNav();
  const events = useAsync(() => feed.getPostsByType("event", 0, 6), []);
  const grid = useAsync(() => feed.getExploreFeed(0, 12), []);

  return (
    <>
      <div className="section-header">
        <span className="t-micro" style={{ color: "var(--text-hint)" }}>
          This week on campus
        </span>
      </div>
      {events.status === "ready" ? (
        <Carousel>
          {events.data.map((p) => (
            <button
              key={p.id}
              type="button"
              className="explore-slide"
              onClick={() => {
                haptic.tap();
                push({ name: "postDetail", postId: p.id });
              }}
            >
              <img src={assetUrl(p.thumbnailURL)} alt="" loading="lazy" />
              <span className="explore-slide__scrim" />
              <span className="explore-slide__label">
                <strong>{p.title}</strong>
                {eventWhen(p.eventDate)}
              </span>
            </button>
          ))}
        </Carousel>
      ) : (
        <Skeleton height={120} radius={12} />
      )}

      {grid.status === "loading" ? (
        <div className="explore-grid" style={{ marginTop: "var(--space-3)" }}>
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} height={180} radius={12} />
          ))}
        </div>
      ) : grid.status === "error" ? (
        <ErrorState onRetry={grid.reload} />
      ) : (
        <div className="explore-grid" style={{ marginTop: "var(--space-3)" }}>
          {grid.data.map((p) => (
            <button
              key={p.id}
              type="button"
              className="explore-tile"
              onClick={() => {
                haptic.tap();
                push({ name: "postDetail", postId: p.id });
              }}
            >
              <img src={assetUrl(p.mediaURL ?? p.thumbnailURL)} alt="" loading="lazy" />
              {/* Scrim + white label are theme-invariant — they sit on the photo (D4). */}
              <span className="explore-tile__scrim" />
              <span className="explore-tile__label">
                {p.content}
                <span className="explore-tile__meta">
                  <i className="fa-regular fa-eye" aria-hidden="true" /> {compactCount(p.viewCount)}
                </span>
              </span>
            </button>
          ))}
        </div>
      )}
    </>
  );
}

/** Paddles + scroll-snap — never free drag (05 gesture register). */
function Carousel({ children }: { children: React.ReactNode }) {
  const track = useRef<HTMLDivElement>(null);

  const step = (dir: 1 | -1) => {
    const node = track.current;
    if (!node) return;
    haptic.tap();
    const slide = node.firstElementChild as HTMLElement | null;
    node.scrollBy({ left: dir * ((slide?.offsetWidth ?? 240) + 10), behavior: "smooth" });
  };

  return (
    <div className="carousel">
      <div className="carousel__track" ref={track}>
        {children}
      </div>
      <button
        type="button"
        className="carousel__paddle carousel__paddle--prev"
        aria-label="Previous"
        onClick={() => step(-1)}
      >
        <i className="fa-solid fa-chevron-left" aria-hidden="true" />
      </button>
      <button
        type="button"
        className="carousel__paddle carousel__paddle--next"
        aria-label="Next"
        onClick={() => step(1)}
      >
        <i className="fa-solid fa-chevron-right" aria-hidden="true" />
      </button>
    </div>
  );
}

/* ---------------- Learn ---------------- */

/**
 * The real one. Mock Learn is gone on every surface — this section renders the
 * content engine's feed via the shared LearnFeed component (one renderer,
 * Distribution §4). In a browser, /learn/<course>/<item> deep links land here
 * with the shared item on screen.
 */
function LearnSection() {
  const path = isHosted ? null : parseLearnPath(window.location.pathname);
  return (
    <LearnFeed initialCourseId={path?.courseId ?? null} initialItemId={path?.itemId ?? null} />
  );
}

/* ---------------- Earn ---------------- */

function EarnSection() {
  const { feed } = useServices();
  const { push } = useNav();
  const load = useAsync(() => feed.getEarnFeed(0, 10), []);

  const [posts, setPosts] = useState<Post[]>([]);
  useEffect(() => {
    if (load.status === "ready") setPosts(load.data);
  }, [load.status, load.data]);

  const actions = useMemo(
    () => makePostActions(feed, setPosts, (post) => push({ name: "postDetail", postId: post.id })),
    [feed, push],
  );

  if (load.status === "loading") return <FeedSkeleton />;
  if (load.status === "error") return <ErrorState onRetry={load.reload} />;
  if (posts.length === 0) {
    return (
      <EmptyState
        title="Nothing pre-qualified yet"
        body="Earn gets sharper as your profile fills in."
      />
    );
  }

  return (
    <>
      <div className="section-header">
        <span className="t-micro" style={{ color: "var(--text-hint)" }}>
          Found for you
        </span>
      </div>
      <div className="stack">
        {posts.map((p) => (
          <PostCard key={p.id} post={p} actions={actions} />
        ))}
      </div>
    </>
  );
}

/* ---------------- Compete ---------------- */

function CompeteSection() {
  const { challenge, feed } = useServices();
  const today = useAsync(() => challenge.getTodayChallenge(), []);
  const board = useAsync(() => challenge.getLeaderboard(), []);
  const rivalsLoad = useAsync(() => feed.getPostsByType("rival", 0, 5), []);

  const [rivals, setRivals] = useState<Post[]>([]);
  useEffect(() => {
    if (rivalsLoad.status === "ready") {
      setRivals(rivalsLoad.data.filter((p) => p.isOpen !== false));
    }
  }, [rivalsLoad.status, rivalsLoad.data]);

  const rivalActions = useMemo(() => makePostActions(feed, setRivals), [feed]);

  const [submitted, setSubmitted] = useState(false);
  const filePicker = useRef<HTMLInputElement>(null);

  const submitClip = async (file: File | undefined) => {
    if (!file || today.status !== "ready") return;
    await challenge.submitChallenge(today.data.id, URL.createObjectURL(file));
    haptic.success();
    setSubmitted(true);
  };

  const openRival = rivals[0];

  return (
    <div className="stack" style={{ marginTop: "var(--space-2)" }}>
      {/* Today's challenge — brand tint, same D2 surface rule as the GPA hero. */}
      <div className="gpa-hero" style={{ margin: 0 }}>
        <div className="t-micro" style={{ color: "var(--brand-tint-text)" }}>
          Today's challenge
        </div>
        {today.status === "ready" ? (
          <>
            <div className="t-heading" style={{ color: "var(--text-strong)" }}>
              {today.data.prompt}
            </div>
            <div className="card__row" style={{ marginTop: 4 }}>
              <Pill tone="reward">
                <i className="fa-solid fa-sparkles" aria-hidden="true" />{" "}
                {today.data.submissionCount} submissions today
              </Pill>
              {submitted ? (
                <Pill tone="state">
                  <i className="fa-solid fa-check" aria-hidden="true" /> Clip in
                </Pill>
              ) : (
                <Button onClick={() => filePicker.current?.click()}>Submit a clip</Button>
              )}
            </div>
            {/* System file picker — no custom camera UI exists or is designed. */}
            <input
              ref={filePicker}
              type="file"
              accept="video/*"
              hidden
              onChange={(e) => void submitClip(e.target.files?.[0])}
            />
          </>
        ) : (
          <Skeleton height={44} radius={8} />
        )}
      </div>

      {board.status === "ready" ? (
        <Card compact>
          <div className="card__body">
            <span className="t-micro" style={{ color: "var(--text-hint)" }}>
              Leaderboard · this week
            </span>
            {board.data.map((row, i) => (
              <div key={row.user.id} className="board-row">
                <span className="board-row__rank">{i + 1}</span>
                <Avatar
                  id={row.user.id}
                  name={row.user.displayName}
                  photoURL={row.user.profilePhotoURL}
                  size={30}
                />
                <span className="board-row__name">
                  {row.user.displayName}
                  {row.user.id === "user-001" ? (
                    <span className="board-row__you"> · you</span>
                  ) : null}
                </span>
                <span className="board-row__points">{count(row.points)}</span>
              </div>
            ))}
          </div>
        </Card>
      ) : (
        <Skeleton height={180} radius={16} />
      )}

      {openRival ? <PostCard post={openRival} compact actions={rivalActions} /> : null}

      <button
        type="button"
        className="btn btn--quick"
        onClick={() => {
          const ok = shareToStory(
            `${window.location.origin}/brand/wordmark.png`,
            "My Freshman streak 🔥",
          );
          if (!ok) showAlert("Story sharing needs a newer Telegram version.");
        }}
      >
        <i className="fa-solid fa-fire" aria-hidden="true" /> Share streak to story
      </button>
    </div>
  );
}

/* ================= Pushed screens ================= */

/* ---------------- Post detail ---------------- */

export function PostDetail({ postId }: { postId: string }) {
  const { feed } = useServices();
  const stored = feed.getPost(postId);
  const [posts, setPosts] = useState<Post[]>(stored ? [stored] : []);
  const comments = useAsync(() => feed.getComments(postId), [postId]);
  const [draft, setDraft] = useState("");
  const [added, setAdded] = useState<{ id: string; text: string; author: string }[]>([]);

  const actions = useMemo(() => makePostActions(feed, setPosts), [feed]);
  const post = posts[0];

  if (!post) {
    return (
      <Screen>
        <EmptyState title="This post is gone" body="It may have been removed by its author." />
      </Screen>
    );
  }

  const send = async () => {
    const text = draft.trim();
    if (!text) return;
    setDraft("");
    const comment = await feed.addComment(postId, text);
    haptic.success();
    setAdded((c) => [
      ...c,
      { id: comment.id, text: comment.text, author: comment.author.displayName },
    ]);
  };

  return (
    <Screen>
      <div style={{ marginTop: "var(--space-3)" }}>
        <PostCard post={post} actions={actions} />
      </div>

      <div className="section-header">
        <span className="t-micro" style={{ color: "var(--text-hint)" }}>
          Comments · {post.commentCount}
        </span>
      </div>

      {comments.status === "loading" ? (
        <Skeleton height={120} radius={12} />
      ) : (
        <div className="stack">
          {(comments.data ?? []).map((c) => (
            <div key={c.id} className="comment-row">
              <Avatar
                id={c.author.id}
                name={c.author.displayName}
                photoURL={c.author.profilePhotoURL}
                size={24}
              />
              <div>
                <span className="comment-row__author">{c.author.displayName}</span>
                <span className="comment-row__text">{c.text}</span>
              </div>
            </div>
          ))}
          {added.map((c) => (
            <div key={c.id} className="comment-row">
              <Avatar
                id="user-001"
                name={c.author}
                photoURL="Characters/alex/alex-profile"
                size={24}
              />
              <div>
                <span className="comment-row__author">{c.author}</span>
                <span className="comment-row__text">{c.text}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="comment-composer">
        <input
          className="field"
          placeholder="Add a comment…"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") void send();
          }}
        />
        <Button onClick={() => void send()} disabled={draft.trim() === ""}>
          Post
        </Button>
      </div>
    </Screen>
  );
}

/* ---------------- Calendar ---------------- */

interface CalEntry {
  id: string;
  date: Date;
  title: string;
  subtitle?: string;
  tone: string;
  allDay: boolean;
}

const DATE_TONE: Record<AcademicDateCategory, string> = {
  officeHours: "var(--mint-600)",
  dropDeadline: "var(--error)",
  examPeriod: "var(--warning)",
  registrationOpen: "var(--blue-800)",
  holiday: "var(--neutral-400)",
};

export function CalendarScreen() {
  const { academic, feed } = useServices();
  const assignments = useAsync(() => academic.getAssignments(), []);
  const dates = useAsync(() => academic.getAcademicDates(), []);
  const events = useAsync(() => feed.getPostsByType("event", 0, 20), []);
  const groups = useAsync(() => academic.getStudyGroups(), []);
  const courses = useAsync(() => academic.getCourses(), []);

  const [selected, setSelected] = useState(() => startOfDay(new Date()));

  const entries = useMemo<CalEntry[]>(() => {
    const all: CalEntry[] = [];
    const courseCode = (id: string) =>
      courses.data?.find((c) => c.id === id)?.code ?? id.toUpperCase();

    for (const a of assignments.data ?? []) {
      if (a.status === "graded") continue;
      const d = new Date(a.dueDate);
      all.push({
        id: `a-${a.id}`,
        date: d,
        title: `${a.title} due`,
        subtitle: `${timeLabel(d)} · ${courseCode(a.courseId)}`,
        tone: "var(--error)",
        allDay: false,
      });
    }
    for (const ad of dates.data ?? []) {
      const d = new Date(ad.date);
      all.push({
        id: `d-${ad.id}`,
        date: d,
        title: ad.title,
        subtitle:
          [ad.courseId ? courseCode(ad.courseId) : null, ad.notes].filter(Boolean).join(" · ") ||
          undefined,
        tone: DATE_TONE[ad.category],
        allDay: ad.category === "holiday" || ad.category === "examPeriod",
      });
    }
    for (const e of events.data ?? []) {
      if (!e.eventDate) continue;
      const d = new Date(e.eventDate);
      all.push({
        id: `e-${e.id}`,
        date: d,
        title: e.title ?? e.content,
        subtitle: `${timeLabel(d)}${e.eventLocation ? ` · ${e.eventLocation}` : ""}${e.isRSVPd ? " · Going" : ""}`,
        tone: "var(--blue-500)",
        allDay: false,
      });
    }
    for (const g of groups.data ?? []) {
      if (!g.nextSession) continue;
      const d = new Date(g.nextSession);
      all.push({
        id: `g-${g.id}`,
        date: d,
        title: g.name,
        subtitle: `${timeLabel(d)} · study group`,
        tone: "var(--mint-600)",
        allDay: false,
      });
    }
    return all.sort((x, y) => x.date.getTime() - y.date.getTime());
  }, [assignments.data, dates.data, events.data, groups.data, courses.data]);

  const week = useMemo(() => {
    const days: Date[] = [];
    const base = startOfDay(new Date());
    for (let i = 0; i < 7; i++) {
      days.push(new Date(base.getTime() + i * 86_400_000));
    }
    return days;
  }, []);

  const dayEntries = entries.filter((e) => sameDay(e.date, selected));

  return (
    <Screen>
      <div className="detail-head">
        <h1 className="t-title">Calendar</h1>
      </div>

      {/* Week strip taps — no swipe; the horizontal edge is left alone. */}
      <div className="week-strip">
        {week.map((d) => {
          const active = sameDay(d, selected);
          const busy = entries.some((e) => sameDay(e.date, d));
          return (
            <button
              key={d.toISOString()}
              type="button"
              className={`week-day${active ? " week-day--active" : ""}`}
              onClick={() => {
                haptic.select();
                setSelected(d);
              }}
            >
              <span className="week-day__name">
                {d.toLocaleDateString(undefined, { weekday: "short" })}
              </span>
              <span className="week-day__num">{d.getDate()}</span>
              {busy ? <span className="week-day__dot" /> : null}
            </button>
          );
        })}
      </div>

      <div className="section-header">
        <span className="t-micro" style={{ color: "var(--text-hint)" }}>
          {selected.toLocaleDateString(undefined, {
            weekday: "long",
            month: "short",
            day: "numeric",
          })}
        </span>
      </div>

      {dayEntries.length === 0 ? (
        <div className="well-empty">Nothing scheduled — a genuinely free day.</div>
      ) : (
        <div className="stack">
          {dayEntries.map((e) => (
            <div key={e.id} className="cal-entry" style={{ borderLeftColor: e.tone }}>
              <div className="course-row__name">{e.title}</div>
              {e.subtitle ? <div className="course-row__meta">{e.subtitle}</div> : null}
            </div>
          ))}
        </div>
      )}

      {dayEntries.length > 0 ? (
        <div style={{ marginTop: "var(--space-4)" }}>
          <Button
            kind="secondary"
            block
            onClick={() => openExternal(googleCalendarUrl(dayEntries[0]))}
          >
            <i className="fa-regular fa-calendar-plus" aria-hidden="true" /> Add to phone calendar
          </Button>
        </div>
      ) : null}
    </Screen>
  );
}

function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function sameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function timeLabel(d: Date): string {
  return d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" }).replace(":00", "");
}

/** A real calendar template link — works from the Telegram webview's browser. */
function googleCalendarUrl(entry: CalEntry): string {
  const stamp = (d: Date) =>
    d
      .toISOString()
      .replace(/[-:]/g, "")
      .replace(/\.\d{3}/, "");
  const end = new Date(entry.date.getTime() + 60 * 60 * 1000);
  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: entry.title,
    dates: `${stamp(entry.date)}/${stamp(end)}`,
    details: entry.subtitle ?? "From Freshman",
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

/* ---------------- Search ---------------- */

export function SearchScreen() {
  const { feed, academic, matching } = useServices();
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 250);
    return () => clearTimeout(t);
  }, [query]);

  const trending = useAsync(() => feed.getTrendingHashtags(), []);
  const courses = useAsync(() => academic.getCourses(), []);
  const matches = useAsync(() => matching.getMatches(0, 10), []);

  const results = useAsync(async () => {
    if (debounced === "") return null;
    const [posts, users, groups] = await Promise.all([
      feed.searchPosts(debounced, 5),
      feed.searchUsers(debounced, 5),
      academic.getStudyGroups(),
    ]);
    return {
      posts,
      users,
      groups: groups.filter((g) => g.name.toLowerCase().includes(debounced.toLowerCase())),
    };
  }, [debounced]);

  const { push } = useNav();

  const q = debounced.toLowerCase();
  const courseHit = (courses.data ?? []).find(
    (c) =>
      q !== "" && ((c.code ?? "").toLowerCase().includes(q) || c.name.toLowerCase().includes(q)),
  );

  const matchIds = new Set((matches.data ?? []).map((m) => m.user.id));

  return (
    <Screen>
      <div style={{ padding: "var(--space-3) 0 var(--space-2)" }}>
        <input
          className="field"
          placeholder="Search courses, people, posts…"
          value={query}
          autoFocus
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>

      {debounced === "" ? (
        <>
          <div className="section-header">
            <span className="t-micro" style={{ color: "var(--text-hint)" }}>
              Trending
            </span>
          </div>
          <div className="card__row">
            {(trending.data ?? []).map((tag) => (
              <button
                key={tag}
                type="button"
                className="chip chip--active"
                onClick={() => setQuery(tag)}
              >
                {tag}
              </button>
            ))}
          </div>
        </>
      ) : (
        <>
          {courseHit ? (
            <>
              <div className="section-header">
                <span className="t-micro" style={{ color: "var(--text-hint)" }}>
                  Top result
                </span>
              </div>
              <Card compact onClick={() => push({ name: "courseDetail", courseId: courseHit.id })}>
                <div className="course-row">
                  <CourseBadge code={courseHit.code ?? courseHit.name} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="course-row__name">{courseHit.name}</div>
                    <div className="course-row__meta">
                      Your course
                      {courseHit.professor ? ` · ${courseHit.professor.displayName}` : ""}
                    </div>
                  </div>
                  <i className="fa-solid fa-chevron-right course-row__chevron" aria-hidden="true" />
                </div>
              </Card>
            </>
          ) : null}

          {results.status === "ready" && results.data ? (
            <>
              <div className="section-header">
                <span className="t-micro" style={{ color: "var(--text-hint)" }}>
                  More
                </span>
              </div>
              <div className="stack">
                {results.data.groups.map((g) => (
                  <Card key={g.id} compact>
                    <div className="course-row">
                      <div style={{ flex: 1 }}>
                        <div className="course-row__name">{g.name}</div>
                        <div className="course-row__meta">{g.memberCount} peers · group chat</div>
                      </div>
                    </div>
                  </Card>
                ))}
                {results.data.users.map((u) => (
                  <Card
                    key={u.id}
                    compact
                    onClick={() => push({ name: "publicProfile", userId: u.id })}
                  >
                    <div className="course-row">
                      <Avatar
                        id={u.id}
                        name={u.displayName}
                        photoURL={u.profilePhotoURL}
                        size={30}
                      />
                      <div style={{ flex: 1 }}>
                        <div className="course-row__name">{u.displayName}</div>
                        <div className="course-row__meta">
                          {matchIds.has(u.id) ? "Match · " : ""}
                          {u.school ?? "UT Austin"}
                        </div>
                      </div>
                      <i
                        className="fa-solid fa-chevron-right course-row__chevron"
                        aria-hidden="true"
                      />
                    </div>
                  </Card>
                ))}
                {results.data.posts.map((p) => (
                  <Card
                    key={p.id}
                    compact
                    onClick={() => push({ name: "postDetail", postId: p.id })}
                  >
                    <div className="course-row">
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div className="course-row__name search-post-line">{p.content}</div>
                        <div className="course-row__meta">
                          {p.author.displayName} · {absoluteDay(p.createdAt)}
                        </div>
                      </div>
                      <i
                        className="fa-solid fa-chevron-right course-row__chevron"
                        aria-hidden="true"
                      />
                    </div>
                  </Card>
                ))}
                {results.data.groups.length +
                  results.data.users.length +
                  results.data.posts.length ===
                  0 && !courseHit ? (
                  <EmptyState title="No results" body="Try a course code, a name, or a hashtag." />
                ) : null}
              </div>
            </>
          ) : results.status === "loading" ? (
            <Skeleton height={120} radius={12} />
          ) : null}
        </>
      )}
    </Screen>
  );
}
